const AWIN = /(^|\.)(awin1\.com|zenaps\.com)$/;
const PARTNERIZE = /(^|\.)(prf\.hn|partnerize\.com|pntra\.com|pntrb\.com|pntrs\.com)$/;
const IMPACT = /(^|\.)(evyy\.net|pxf\.io|sjv\.io|impact\.com|impactradius\.com)$/;
/** Feature.fm's click tracker: every link of a Feature.fm bio page
 *  (`ffm.bio`) goes through it, its destination inside its `cd` data. */
const FEATURE_FM = /^api\.ffm\.to$/;

/** Whether an address belongs to a known affiliate tracker, rather than
 * the seller it will send a reader to. */
export function isAffiliateUrl(value: string): boolean {
  try {
    const host = new URL(value).hostname.toLowerCase();
    return AWIN.test(host) || PARTNERIZE.test(host) || IMPACT.test(host) || FEATURE_FM.test(host);
  } catch {
    return false;
  }
}

/** The destination a tracker's address carries, still encoded as published;
 *  null for another address, or a tracker's that carries none. */
function publishedDestination(url: URL): string | null {
  const host = url.hostname.toLowerCase();
  if (AWIN.test(host)) return url.searchParams.get("ued") || url.searchParams.get("p");
  if (IMPACT.test(host)) return url.searchParams.get("u");
  if (FEATURE_FM.test(host)) return featureFmDestination(url.searchParams.get("cd"));
  if (!PARTNERIZE.test(host)) return null;
  const path = url.pathname.match(/\/destination:(.+)$/)?.[1];
  // Partnerize sends the rest of its address on: an unencoded destination
  // keeps its own query and fragment.
  if (path && /^https?:\/\//i.test(path)) return path + url.search + url.hash;
  return url.searchParams.get("destination") || path || null;
}

/** The destination in Feature.fm's click data: base64url JSON whose
 *  `destUrl` is where the link leads. Null when it carries none. */
function featureFmDestination(data: string | null): string | null {
  if (!data) return null;
  try {
    const destination = (JSON.parse(Buffer.from(data, "base64url").toString("utf8")) as { destUrl?: unknown }).destUrl;
    return typeof destination === "string" && destination ? destination : null;
  } catch {
    return null;
  }
}

/** The published destination in an affiliate link, without visiting its
 * tracker. Unknown links and wrappers without a usable destination stay as
 * published, so the normal page reader can still follow their redirects. */
export function unwrapAffiliateUrl(value: string): string {
  let current = value;
  const seen = new Set<string>();
  while (!seen.has(current)) {
    seen.add(current);
    let url: URL;
    try {
      url = new URL(current.trim());
    } catch {
      break;
    }
    if (!/^https?:$/.test(url.protocol) || url.username || url.password) break;
    let destination = publishedDestination(url);
    if (!destination) break;
    // A path destination is percent-encoded; query values have already
    // been decoded once by URLSearchParams. Never decode a valid URL's own
    // query again (its seller parameters may contain encoded '&' or '/').
    while (!/^https?:\/\//i.test(destination)) {
      try {
        const decoded = decodeURIComponent(destination);
        if (decoded === destination) break;
        destination = decoded;
      } catch {
        break;
      }
    }
    try {
      const target = new URL(destination);
      if (!/^https?:$/.test(target.protocol) || target.username || target.password) break;
      current = target.href;
    } catch {
      break;
    }
  }
  return current;
}
